import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  ButtonGroup,
  FormControlLabel,
  FormHelperText,
  Grid,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

import moment from "moment";
import { Formik } from "formik";
import { AuthContext } from "../../../components/user/BCPAuthProvider";
import { postNotification } from "../../../components/utilities/Notifications";
import {
  getGameSystems,
  handlePaging,
  handlePagingAll,
} from "../../../components/api/APIUtilities";

import TextFieldSelect from "../../../components/utilities/TextFieldSelect";
import ListResponsive from "../../../components/other/ListResponsive";
import ModalLoadingFull from "../../../components/modals/ModalLoadingFull";
import StyledMenu from "../../../components/layout/StyledMenu";

import ArmyListCard from "./ArmyListCard";
import MUITags from "../../../components/utilities/MUITags";
import { globalVariables } from "../../../global/GlobalVariables";
import DatePickerComponent from "../../../components/utilities/DatePickerComponent";
import GenericPicker from "../../../components/utilities/GenericPicker";
import GameSystemPicker from "../../../components/utilities/gamesystempickers/GameSystemPicker";
import {
  BCPAngleDown,
  BCPAngleRight,
  BCPAnglesDown,
  BCPAnglesUp,
} from "../../../components/icons/bcp/BCPIcons";
import { getEnvironmentVariable } from "../../../global/environmentVariables";
import {
  getLocalStorage,
  setLocalStorage,
} from "../../../components/layout/cookies/CookieFunctions";

const IS_DEMO = getEnvironmentVariable("IS_DEMO") === "true";
const SUPPORTS_CARD_DATABASE =
  getEnvironmentVariable("SUPPORTS_CARD_DATABASE") === "true";
const SINGLE_GAME_SYSTEM = getEnvironmentVariable("SINGLE_GAME_SYSTEM");
const SINGLE_GAMETYPE = getEnvironmentVariable("SINGLE_GAMETYPE");

const blankSearch = {
  nameSearch: "",
  gameSystem: "",
  deckFormat: "Standard",
  resourceSymbols: [],
  factions: [],
  tags: [],
  tagsList: "",

  startDate: null,
  endDate: null,

  eventLists: false,
  gameSystems: [],
  eventSubType: "",
  location: "Global",
  selectedAddress: "",
  selectedLocation: null,
  distance: 50,

  fuzzy: IS_DEMO,
  showPublicOnly: true,

  sortKey: "_created_at",
  sortAscending: false,
};

const sortOptions = {
  _created_at: "Created",
};

export default function ArmyListSearchWidget(props) {
  const {
    gameSystem,
    userSearch,
    cardUtil,
    heading,
    containerSX,
    dense,
    imageCache,
    resultsHelperText,
  } = props;

  const authContext = useContext(AuthContext);
  const globalVars = globalVariables();

  const [geoLocation] = useState(null);

  const [loadingPage, setLoadingPage] = useState(true);
  const [armies, setArmies] = useState(props.armies);

  var initalGameSystems = [];
  if (authContext?.authState?.cookieConsent) {
    var getGetCachedData = getLocalStorage(`gameSystems`, authContext);
    if (getGetCachedData) {
      initalGameSystems = JSON.parse(getGetCachedData);
    }
  }
  const [gameSystems, setGameSystems] = useState(initalGameSystems);

  const [isSearching, setIsSearching] = useState(null);
  const [advancedSearch, setAdvancedSearch] = useState(false);

  const [searchValues, setSearchValues] = useState(blankSearch);
  const [sortAnchor, setSortAnchor] = useState(null);

  const [armyLists, setArmyLists] = useState(null);
  const [nextKey, setNextKey] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [filtersState, setFiltersState] = useState(null);

  const fetchArmyLists = async () => {
    if (!filtersState) {
      postNotification("error", "No filters selected.");
      return;
    }

    try {
      var apiArmyLists = await handlePaging(
        "/armylists",
        filtersState,
        nextKey,
        setNextKey
      );

      setArmyLists(armyLists ? [...armyLists, ...apiArmyLists] : apiArmyLists);

      if (apiArmyLists.length !== filtersState.limit) {
        setHasMore(false);
        if (!apiArmyLists?.length) {
          postNotification(
            "info",
            `No ${
              gameSystem?.listLabel ? `${gameSystem?.listLabel}s` : "lists"
            } found matching your search criteria.`
          );
          setIsSearching(false);
          return;
        }
      }
    } catch (err) {
      postNotification("error", err);
    }

    setIsSearching(false);
  };

  useEffect(() => {
    if (filtersState) {
      fetchArmyLists();
    }
    // eslint-disable-next-line
  }, [filtersState]);

  useEffect(() => {
    const loadPage = async () => {
      setLoadingPage(true);

      if (userSearch) {
        setIsSearching(true);
        setNextKey(null);
        setHasMore(true);
        setArmyLists([]);

        var filters = {
          limit: 100,
          sortKey: "_created_at",
          sortAscending: false,
          userId: authContext?.authState?.identity?.userId,
          fuzzy: true,
          includeNonPublic: true,
          "expand[]": ["character"],
        };

        if (SINGLE_GAME_SYSTEM) {
          filters.gameSystemId = SINGLE_GAME_SYSTEM;
        }

        setFiltersState(filters);

        setLoadingPage(false);
        return;
      }

      setSearchValues({
        ...searchValues,
        location: "Global",
      });

      var apiArmies = await handlePagingAll(`/armies`, {
        gameType: gameSystem?.code || SINGLE_GAMETYPE,
      });
      setArmies(apiArmies);

      setLoadingPage(false);

      var data = await getGameSystems();
      setGameSystems(data);
      if (authContext?.authState?.cookieConsent) {
        try {
          setLocalStorage("gameSystems", JSON.stringify(data), authContext);
          var getCachedItems = getLocalStorage("CachedItems", authContext);
          var cachedItems = getCachedItems ? JSON.parse(getCachedItems) : {};
          cachedItems["gameSystems"] = true;
          setLocalStorage(
            "CachedItems",
            JSON.stringify(cachedItems),
            authContext
          );
        } catch (err) {}
      }
    };
    loadPage();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      {!loadingPage && (
        <>
          <Grid container sx={{ gap: 2, ...containerSX }}>
            {heading && (
              <Grid
                item
                xs={12}
                sx={{
                  p: 2,
                  display: "flex",
                }}
              >
                <Grid item sx={{ flex: 1 }}>
                  <Typography variant="h3">{heading}</Typography>
                </Grid>
                <Grid item sx={{ textAlign: "right" }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    href="/deck-builder"
                  >
                    Deck Builder
                  </Button>
                </Grid>
              </Grid>
            )}
            {!userSearch && (
              <Grid
                item
                xs={12}
                sx={{
                  p: 2,
                }}
              >
                <Formik
                  initialValues={searchValues}
                  enableReinitialize
                  onSubmit={async (values) => {
                    try {
                      setIsSearching(true);
                      setNextKey(null);
                      setHasMore(true);
                      setArmyLists([]);

                      var filters = {
                        limit: 100,
                        sortKey: values.sortKey || undefined,
                        sortAscending: values.sortAscending,
                        "expand[]": [
                          "gameSystem",
                          "user",
                          "event",
                          "player",
                          "army",
                          "subFaction",
                          "character",
                        ],
                      };
                      if (
                        userSearch &&
                        authContext?.authState?.identity?.userId
                      ) {
                        filters.userId =
                          authContext?.authState?.identity?.userId;
                      }
                      if (values?.startDate && values?.startDate !== "") {
                        filters.startDate = moment(
                          values.startDate,
                          "YYYY-MM-DD HH:mm"
                        )
                          .utc()
                          .format();
                      }
                      if (values?.endDate && values?.endDate !== "") {
                        filters.endDate = moment(
                          values.endDate,
                          "YYYY-MM-DD HH:mm"
                        )
                          .utc()
                          .format();
                      }
                      if (
                        filters?.startDate &&
                        filters?.endDate &&
                        moment(filters.startDate).isAfter(
                          moment(filters.endDate)
                        )
                      ) {
                        throw new Error(
                          "Start Date must be before the End Date"
                        );
                      }

                      if (SINGLE_GAME_SYSTEM) {
                        filters.gameSystemId = SINGLE_GAME_SYSTEM;
                      }
                      if (gameSystem?.id) {
                        filters.gameSystemId = gameSystem.id;
                      }
                      if (values.gameSystems?.length) {
                        filters.gameSystems = values.gameSystems;
                      }
                      if (values.deckFormat !== "") {
                        filters.deckFormat = values.deckFormat;
                      }
                      if (values.nameSearch !== "") {
                        filters.nameSearch = values.nameSearch;
                      }

                      if (!values.showPublicOnly) {
                        filters.includeNonPublic = true;
                      }

                      if (values.resourceSymbols?.length) {
                        filters["armyIds[]"] = values.resourceSymbols.map(
                          (name) =>
                            armies.filter((army) => army.name === name)[0]?.id
                        );
                      }
                      if (values.factions?.length) {
                        filters["armyIds[]"] = values.factions.map(
                          (name) =>
                            armies.filter((army) => army.name === name)[0]?.id
                        );
                      }
                      if (values.tags.length) {
                        filters["tags[]"] = values.tags;
                      }

                      if (values.eventLists) {
                        filters.eventLists = true;

                        if (values.eventSubType && values.eventSubType !== "") {
                          filters.eventSubType = values.eventSubType;
                        }

                        var location;

                        if (values.location === "Current Location") {
                          location = {
                            distance: values.distance,
                            center: {
                              lat: geoLocation.latitude,
                              long: geoLocation.longitude,
                            },
                          };
                        } else if (values.location === "Online Only") {
                          filters.onlineOnly = true;
                        } else if (
                          values.location !== "Global" &&
                          values.selectedLocation?.coordinate?.length === 2
                        ) {
                          location = {
                            distance: values.distance,
                            center: {
                              lat: values.selectedLocation.coordinate[1],
                              long: values.selectedLocation.coordinate[0],
                            },
                          };
                        }

                        if (location) {
                          filters.location = JSON.stringify(location);
                        }
                      }

                      setSearchValues(values);
                      setFiltersState(filters);
                    } catch (error) {
                      postNotification("error", error);
                      setIsSearching(false);
                    }
                  }}
                >
                  {({
                    errors,
                    handleBlur,
                    handleChange,
                    handleSubmit,
                    touched,
                    values,
                    setFieldValue,
                    setFieldTouched,
                    handleReset,
                  }) => (
                    <form onSubmit={handleSubmit}>
                      <Grid container spacing={2}>
                        {cardUtil?.deckFormats?.length > 0 && (
                          <Grid item xs={12}>
                            <TextFieldSelect
                              list={cardUtil.deckFormats.map(
                                (deckFormat) => deckFormat.name
                              )}
                              name="deckFormat"
                              label="Format"
                              value={values.deckFormat}
                              fullWidth
                              variant="outlined"
                              margin="none"
                              setFieldValue={setFieldValue}
                              setFieldTouched={setFieldTouched}
                              error={Boolean(
                                touched.deckFormat && errors.deckFormat
                              )}
                              helperText={
                                touched.deckFormat && errors.deckFormat
                              }
                              clearAll
                            />
                          </Grid>
                        )}

                        <Grid item xs={12}>
                          <TextField
                            name="nameSearch"
                            label="Name Search"
                            value={values.nameSearch}
                            variant="outlined"
                            fullWidth
                            margin="none"
                            onBlur={handleBlur}
                            onChange={handleChange}
                            error={Boolean(
                              touched.nameSearch && errors.nameSearch
                            )}
                            helperText={touched.nameSearch && errors.nameSearch}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <FormControlLabel
                            label={
                              <Typography className="MuiFormControlLabel-label">
                                Show Event{" "}
                                {SUPPORTS_CARD_DATABASE ? "Decks" : "Lists"}{" "}
                                Only
                              </Typography>
                            }
                            sx={{
                              ml: 0,
                              width: "100%",
                              "& .MuiFormControlLabel-label": {
                                width: "calc(100% - 58px)",
                              },
                            }}
                            control={
                              <Switch
                                color="primary"
                                checked={values.eventLists}
                                onChange={(e) => {
                                  setFieldValue(
                                    "eventLists",
                                    !values.eventLists
                                  );
                                }}
                              />
                            }
                            labelPlacement="start"
                          />
                        </Grid>

                        {authContext?.authState?.identity?.isAdmin === true && (
                          <Grid item xs={12}>
                            <FormControlLabel
                              label={
                                <Typography className="MuiFormControlLabel-label">
                                  Show public only
                                </Typography>
                              }
                              sx={{
                                ml: 0,
                                width: "100%",
                                "& .MuiFormControlLabel-label": {
                                  width: "calc(100% - 58px)",
                                },
                              }}
                              control={
                                <Switch
                                  color="primary"
                                  checked={values.showPublicOnly}
                                  onChange={(e) => {
                                    setFieldValue(
                                      "showPublicOnly",
                                      !values.showPublicOnly
                                    );
                                  }}
                                />
                              }
                              labelPlacement="start"
                            />
                          </Grid>
                        )}

                        <Grid
                          item
                          xs={12}
                          onClick={() => {
                            setAdvancedSearch(!advancedSearch);
                          }}
                          sx={{
                            "&:hover": {
                              cursor: "pointer",
                            },
                          }}
                        >
                          <Typography
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              flexWrap: "wrap",
                            }}
                          >
                            {advancedSearch ? (
                              <BCPAngleDown />
                            ) : (
                              <BCPAngleRight />
                            )}
                            <span>Advanced Search</span>
                          </Typography>
                        </Grid>
                        {advancedSearch && (
                          <>
                            <Grid item xs={12}>
                              <MUITags
                                label="Tags"
                                value={values.tagsList}
                                name="tagsList"
                                valueArray={values.tags}
                                nameArray="tags"
                                setFieldValue={setFieldValue}
                                handleBlur={handleBlur}
                                handleChange={handleChange}
                                error={Boolean(
                                  touched.tagsList && errors.tagsList
                                )}
                                helperText={touched.tagsList && errors.tagsList}
                                fullWidth
                                variant="outlined"
                                margin="none"
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <TextFieldSelect
                                list={armies.map((army) => {
                                  return {
                                    value: army.name,
                                    label: army.name,
                                  };
                                })}
                                renderValue={(selected) => {
                                  return <>{selected.map((value) => value)}</>;
                                }}
                                name={"factions"}
                                label={"Factions"}
                                value={values.factions}
                                fullWidth
                                variant="outlined"
                                margin="none"
                                setFieldValue={setFieldValue}
                                setFieldTouched={setFieldTouched}
                                error={Boolean(
                                  touched.factions && errors.factions
                                )}
                                helperText={touched.factions && errors.factions}
                                multiple
                                clearAll
                              />
                            </Grid>

                            {values.eventLists && (
                              <>
                                {!SINGLE_GAME_SYSTEM && (
                                  <Grid item xs={12} sm={6}>
                                    <GameSystemPicker
                                      name="gameType"
                                      value={values.gameType}
                                      handleChange={handleChange}
                                      gameSystems={gameSystems}
                                      touched={touched.gameType}
                                      error={errors.gameType}
                                      allowAny
                                    />
                                  </Grid>
                                )}
                                {globalVars?.eventSubTypes?.list?.length >
                                  0 && (
                                  <Grid
                                    item
                                    xs={12}
                                    sm={SINGLE_GAME_SYSTEM ? 12 : 6}
                                  >
                                    <GenericPicker
                                      name="eventSubType"
                                      label={
                                        globalVars?.eventSubTypes?.label ||
                                        "Event Sub Type"
                                      }
                                      value={values.eventSubType}
                                      handleChange={handleChange}
                                      list={globalVars.eventSubTypes.list || []}
                                      inclAny={true}
                                    />
                                  </Grid>
                                )}
                              </>
                            )}

                            <Grid item xs={12} sm={6}>
                              <DatePickerComponent
                                name="startDate"
                                label="Start Date"
                                variant="outlined"
                                margin="none"
                                value={values.startDate}
                                setFieldValue={setFieldValue}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <DatePickerComponent
                                name="endDate"
                                label="End Date"
                                variant="outlined"
                                margin="none"
                                value={values.endDate}
                                setFieldValue={setFieldValue}
                              />
                            </Grid>
                          </>
                        )}

                        <Grid item xs={12} md={4}>
                          <ButtonGroup variant="outlined" fullWidth>
                            <Button
                              sx={{ maxWidth: "60px" }}
                              onClick={() => {
                                setFieldValue(
                                  "sortAscending",
                                  !values.sortAscending
                                );
                              }}
                            >
                              {values.sortAscending ? (
                                <BCPAnglesUp />
                              ) : (
                                <BCPAnglesDown />
                              )}
                            </Button>

                            <Button
                              onClick={(e) => {
                                setSortAnchor(e.currentTarget);
                              }}
                            >
                              Sort by {sortOptions[values.sortKey]}
                            </Button>
                          </ButtonGroup>
                          <SortMenu
                            setFieldValue={setFieldValue}
                            sortKey={values.sortKey}
                            anchorEl={sortAnchor}
                            setAnchorEl={setSortAnchor}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                          <Button
                            fullWidth
                            variant="outlined"
                            color="error"
                            onClick={(e) => {
                              setSearchValues(blankSearch);
                              setAdvancedSearch(false);
                              handleReset(e);
                            }}
                          >
                            Reset
                          </Button>
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                          <Button
                            fullWidth
                            variant="contained"
                            color="success"
                            type="submit"
                          >
                            Search
                          </Button>
                        </Grid>
                      </Grid>
                    </form>
                  )}
                </Formik>
              </Grid>
            )}

            {dense && armyLists?.length > 0 && (
              <Grid
                item
                xs={12}
                sx={{
                  p: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    height: "300px",
                    overflow: "auto",
                    contain: "strict",
                    overflowY: "hidden",
                  }}
                >
                  {armyLists.map((armyList, index) => (
                    <ArmyListCard
                      key={index}
                      armyList={armyList}
                      dense={dense}
                      imageCache={imageCache}
                    />
                  ))}
                </Box>
                {resultsHelperText && (
                  <FormHelperText>{resultsHelperText}</FormHelperText>
                )}
              </Grid>
            )}

            {!dense && armyLists?.length > 0 && (
              <Grid item xs={12}>
                <ListResponsive
                  originalList={armyLists}
                  loadMore={fetchArmyLists}
                  hasMore={hasMore}
                  scrollToTop
                  sx={{
                    flexFlow: "row wrap",
                    boxSizing: "border-box",
                    display: "flex",
                    placeContent: "center space-between",
                    alignItems: "center",
                  }}
                  rowProps={{
                    mapFunction: (row, index) => (
                      <ArmyListCard
                        key={index}
                        armyList={row}
                        dense={dense}
                        imageCache={imageCache}
                      />
                    ),
                  }}
                />
              </Grid>
            )}
          </Grid>
        </>
      )}
      {(loadingPage || isSearching) && <ModalLoadingFull />}
    </>
  );
}

function SortMenu(props) {
  const { setFieldValue, sortKey, anchorEl, setAnchorEl } = props;
  const open = Boolean(anchorEl);

  return (
    <StyledMenu
      id="sort-options-menu"
      disableScrollLock
      anchorEl={anchorEl}
      open={open}
      onClose={() => setAnchorEl(null)}
    >
      {Object.keys(sortOptions).map((sortOption) => {
        return (
          <MenuItem
            key={sortOption}
            selected={sortOption === sortKey}
            onClick={(e) => {
              setFieldValue("sortKey", sortOption);
              setAnchorEl(null);
            }}
            disableRipple
          >
            Sort by {sortOptions[sortOption]}
          </MenuItem>
        );
      })}
    </StyledMenu>
  );
}
